import { useQuery } from "@tanstack/react-query";
import { Activity, AlertCircle, Euro, RefreshCw, TrendingDown, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { type DepartmentRiskProfile, employeesApi } from "../lib/api.js";

const CURRENT_YEAR = new Date().getFullYear();

const fmtEur = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({
  icon,
  label,
  value,
  accent,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: string;
  sub?: string;
}) {
  return (
    <div className="card px-4 py-3.5" style={{ borderLeft: `2px solid ${accent}` }}>
      <div className="flex items-center gap-1.5 mb-2" style={{ color: accent }}>
        {icon}
        <span
          className="text-[10px] font-medium tracking-widest uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          {label}
        </span>
      </div>
      <div
        className="text-2xl font-bold leading-none font-mono-data"
        style={{ color: "var(--text-primary)" }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function DeptScenarioRow({
  dept,
  billing,
  atRiskRate,
  onRateChange,
}: {
  dept: DepartmentRiskProfile;
  /** Jahresumsatz der Abteilung. undefined = für diese Abteilung ist kein Umsatz bekannt. */
  billing: number | undefined;
  atRiskRate: number;
  onRateChange: (rate: number) => void;
}) {
  const departures = Math.round((atRiskRate / 100) * dept.total);
  const billingAtRisk =
    billing !== undefined && dept.total > 0 ? (departures / dept.total) * billing : null;

  const riskColor =
    atRiskRate >= 40 ? "var(--risk-high)" : atRiskRate >= 20 ? "var(--accent)" : "var(--risk-low)";

  // Gradient for the range track fill — updated via CSS custom property
  const trackStyle = {
    "--track-pct": `${atRiskRate}%`,
    "--track-color": riskColor,
  } as React.CSSProperties;

  return (
    <div
      className="card px-5 py-4"
      style={{ borderLeft: `2px solid ${riskColor}`, transition: "border-color 0.3s ease" }}
    >
      {/* Top row: dept name + departures badge */}
      <div className="flex items-start justify-between mb-3 gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
            {dept.department}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
              {dept.total} Mitarbeiter
            </span>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-mono-data"
              style={{
                background: "var(--bg-hover)",
                color: "var(--text-muted)",
                border: "1px solid var(--border)",
              }}
            >
              {dept.high} hoch · {dept.medium} mittel · {dept.low} niedrig
            </span>
          </div>
        </div>

        {/* Impact summary */}
        <div className="shrink-0 text-right">
          <div
            className="text-xl font-bold font-mono-data leading-none"
            style={{ color: riskColor, transition: "color 0.3s ease" }}
          >
            {departures}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            Abgänge
          </div>
          {billingAtRisk !== null ? (
            <div
              className="text-xs font-mono-data mt-1 font-semibold"
              style={{ color: atRiskRate >= 20 ? riskColor : "var(--text-secondary)" }}
            >
              {fmtEur.format(billingAtRisk)}
            </div>
          ) : (
            <div
              className="text-[10px] mt-1"
              style={{ color: "var(--text-muted)", fontStyle: "italic" }}
            >
              Kein Umsatz
            </div>
          )}
        </div>
      </div>

      {/* Slider */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-medium" style={{ color: "var(--text-muted)" }}>
            ANGENOMMENE ABGANGSQUOTE
          </span>
          <span
            className="text-xs font-bold font-mono-data"
            style={{ color: riskColor, transition: "color 0.3s ease" }}
          >
            {atRiskRate}%
          </span>
        </div>
        <div className="relative">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={atRiskRate}
            onChange={(e) => onRateChange(Number(e.target.value))}
            className="scenario-slider w-full"
            style={trackStyle}
            aria-label={`Abgangsquote für ${dept.department}`}
          />
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ScenarioPlanning() {
  const {
    data: teamsData,
    isLoading: teamsLoading,
    isError: teamsError,
    refetch: refetchTeams,
    isFetching: teamsFetching,
  } = useQuery({
    queryKey: ["teams"],
    queryFn: employeesApi.getTeams,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ["billing", CURRENT_YEAR],
    queryFn: () => employeesApi.getBilling(CURRENT_YEAR),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const teams = teamsData?.teams ?? [];
  // F17: Vorher wurde hier über das Salesforce-Feld `ownerDepartment` gruppiert.
  // Das ist bei JEDEM Berater leer, die Tabelle blieb deshalb leer und die Seite
  // zeigte bei allen Abteilungen "Kein Umsatz". Die Zuordnung kommt jetzt aus der
  // Brücke (Umsatz → Personio-Mensch → dessen Abteilung).
  const billingByDept = useMemo(() => {
    const map = new Map<string, number>();
    for (const [dept, werte] of Object.entries(billingData?.byDepartment ?? {})) {
      // Ehemalige zählen mit: ihr Umsatz war real und gehört in die Abteilung.
      map.set(dept, werte.assigned + werte.former);
    }
    return map;
  }, [billingData]);

  // Per-department slider state — default = high / total × 100
  const [rates, setRates] = useState<Record<string, number>>({});

  function getRate(dept: DepartmentRiskProfile): number {
    if (dept.department in rates) return rates[dept.department];
    if (dept.total === 0) return 0;
    return Math.round((dept.high / dept.total) * 100);
  }

  function setRate(deptName: string, rate: number) {
    setRates((prev) => ({ ...prev, [deptName]: rate }));
  }

  // Summary aggregations
  const { totalDepartures, totalBillingAtRisk, totalHeadcount } = useMemo(() => {
    let dep = 0;
    let billing = 0;
    let headcount = 0;
    for (const team of teams) {
      const rate = getRate(team);
      const departures = Math.round((rate / 100) * team.total);
      dep += departures;
      headcount += team.total;
      const b = billingByDept.get(team.department);
      if (b !== undefined && team.total > 0) {
        billing += (departures / team.total) * b;
      }
    }
    return { totalDepartures: dep, totalBillingAtRisk: billing, totalHeadcount: headcount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, rates, billingByDept]);

  const isLoading = teamsLoading || billingLoading;

  return (
    <AppLayout>
      <Header title="Szenario-Planung" subtitle="Fluktuationsrisiko simulieren" />

      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            Simuliere Abgangsquoten je Abteilung und sieh das aggregierte Risiko.
          </span>
          <button
            type="button"
            onClick={() => setRates({})}
            className="ml-auto text-xs px-3 py-1.5 rounded transition-colors"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--border-light)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            Zurücksetzen
          </button>
          <button
            type="button"
            onClick={() => void refetchTeams()}
            disabled={teamsFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--border-light)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            <RefreshCw size={11} className={teamsFetching ? "animate-spin" : ""} />
            {teamsFetching ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {/* Summary KPI cards */}
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <SummaryCard
            icon={<Users size={14} />}
            label="Headcount gesamt"
            value={isLoading ? "—" : String(totalHeadcount)}
            accent="var(--text-secondary)"
            sub="alle Abteilungen"
          />
          <SummaryCard
            icon={<TrendingDown size={14} />}
            label="Abgänge gesamt (Simulation)"
            value={isLoading ? "—" : String(totalDepartures)}
            accent={
              totalDepartures / Math.max(totalHeadcount, 1) >= 0.3
                ? "var(--risk-high)"
                : "var(--accent)"
            }
            sub={
              totalHeadcount > 0
                ? `${((totalDepartures / totalHeadcount) * 100).toFixed(0)}% des Headcounts`
                : undefined
            }
          />
          <SummaryCard
            icon={<Euro size={14} />}
            label="Umsatzrisiko (Jahreskurs)"
            value={isLoading ? "—" : fmtEur.format(totalBillingAtRisk)}
            accent="var(--accent-gold)"
            sub="basierend auf Projektumsatz"
          />
        </div>

        {/* Was sich keinem Menschen zuordnen ließ — bewusst sichtbar.
            Erscheint nur, wenn es etwas zu melden gibt: Eine Zeile, die immer
            da steht, wird nach zwei Wochen nicht mehr gelesen. */}
        {/* Nur diese eine Zeile, bewusst (Marie, 28.08.2026): Ehemalige und
            Externe sind eine Controlling-Frage, nicht die des Tools. Diese hier
            schützt die eigenen Zahlen — sie springt an, wenn der Umsatz einer
            Mitarbeiterin wegen eines geänderten Namens nicht mehr gefunden wird. */}
        {billingData?.summary && billingData.summary.unassignedAmount > 0 && (
          <div className="card px-4 py-3 mb-4 text-xs" style={{ color: "var(--risk-high)" }}>
            Nicht zugeordnet: {fmtEur.format(billingData.summary.unassignedAmount)} ·{" "}
            {billingData.summary.unassignedCount} Salesforce-Konten ohne Personio-Treffer. Prüfen,
            ob dahinter ein Mitarbeiter mit geändertem Namen steckt.
          </div>
        )}

        {/* Error state */}
        {teamsError && (
          <div className="card flex items-center gap-2 px-4 py-3 mb-4">
            <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
              Team-Daten konnten nicht geladen werden.
            </span>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <RefreshCw size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Abteilungsdaten werden geladen…
            </p>
          </div>
        )}

        {/* Department rows */}
        {!isLoading &&
          !teamsError &&
          (teams.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Activity size={24} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Keine Abteilungsdaten verfügbar.
              </p>
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
              {teams.map((dept) => (
                <DeptScenarioRow
                  key={dept.department}
                  dept={dept}
                  billing={billingByDept.get(dept.department)}
                  atRiskRate={getRate(dept)}
                  onRateChange={(r) => setRate(dept.department, r)}
                />
              ))}
            </div>
          ))}
      </div>

      {/* Slider CSS */}
      <style>{`
        .scenario-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 2px;
          outline: none;
          cursor: pointer;
          background: linear-gradient(
            to right,
            var(--track-color, var(--accent)) 0%,
            var(--track-color, var(--accent)) var(--track-pct, 0%),
            var(--bg-hover) var(--track-pct, 0%),
            var(--bg-hover) 100%
          );
          transition: background 0.2s ease;
        }
        .scenario-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--track-color, var(--accent));
          border: 2px solid var(--bg-card);
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
        }
        .scenario-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        .scenario-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--track-color, var(--accent));
          border: 2px solid var(--bg-card);
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          cursor: pointer;
        }
        .scenario-slider:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
      `}</style>
    </AppLayout>
  );
}
